<template>
  <div class="main">
    <div class="timeline-container desktop">
      <div class="timeline-buttons">
        <TimelineButton
          v-for="(label, index) in tabs"
          :key="index"
          :label="label"
          :isActive="activeTab === index + 1"
          @click="setTimeFrame(index + 1)"
        />
        <DateRangePicker
          :isActive="activeTab === 7"
          @activate="setActiveTab(7)"
          @select="onCustomSelected"
          @close="setActiveTab(0)"
        />
      </div>
      <p class="selected-timeline">{{ selectedTimeline }}</p>
    </div>
    <div class="timeline-container-mobile d-none">
      <div class="timeline-buttons">
        <DateRangePicker
          :isActive="activeTab === 7"
          @activate="setActiveTab(7)"
          @select="onCustomSelected"
          @close="setActiveTab(0)"
        />
        <TimelineButton
          v-for="(label, index) in tabs"
          :key="index"
          :label="label"
          :isActive="activeTab === index + 1"
          @click="setTimeFrame(index + 1)"
        />
      </div>
    </div>
  </div>
</template>

<script>
import TimeManager from "@/Services/TimeManager";
import TimelineButton from "./TimelineButton.vue";
import DateRangePicker from "./DatePicker.vue";
import { responses } from "@/Services/responses";

export default {
  name: "Timeline",
  components: {
    TimelineButton,
    DateRangePicker,
  },
  mounted() {
    // Initialize activeTab from Vuex state on mount
    this.setActiveTab(this.$store.getters.getActiveTimeTab);
  },
  computed: {
    selectedTimeline() {
      const timeLine = this.$store.getters.getTimeFrame || {};
      // Prefer normalized { from, to } which we now commit
      if (timeLine.from && timeLine.to) {
        return `From: ${timeLine.from} To: ${timeLine.to}`;
      }
      // Legacy fallback: shape with startTime/endTime; display with seconds
      if (timeLine.startTime && timeLine.endTime) {
        const s = new Date(timeLine.startTime);
        const e = new Date(timeLine.endTime);
        const pad = (n) => String(n).padStart(2, '0');
        const fmt = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
        return `From: ${fmt(s)} To: ${fmt(e)}`;
      }
      return '';
    },
    activeTimeTab() {
      return this.$store.getters.getActiveTimeTab;
    },
  },
  data() {
    return {
      activeTab: this.$store.getters.getActiveTimeTab,
      tabs: [
        "Today",
        "Yesterday",
        "Last 7 Days",
        "Last Month",
        "This Month",
        "This Year",
      ],
    };
  },
  props: {
    methodBeingCalled: {
      type: Function,
      required: true,
    },
  },
  methods: {
    /**
     * Normalize a date range-like object into { from, to } strings using yyyy-MM-dd HH:mm:ss.
     * Accepts shapes: {startTime,endTime} | {start,end} | {from,to} | Date | string | number
     */
    normalizeRange(range) {
      const pad = (n) => String(n).padStart(2, '0');
      const atStartOfDay = (d) => d.getHours() === 0 && d.getMinutes() === 0 && d.getSeconds() === 0 && d.getMilliseconds() === 0;
      const fmt = (date, endOfDay = false) => {
        const d = new Date(date);
        if (Number.isNaN(d.getTime())) return null;
        if (endOfDay) d.setHours(23, 59, 59, 999); else d.setHours(0, 0, 0, 0);
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
      };

      const startRaw = range && (range.startTime ?? range.start ?? range.from ?? range.fromDate ?? range.start_date ?? range.from_date);
      const endRaw = range && (range.endTime ?? range.end ?? range.to ?? range.toDate ?? range.end_date ?? range.to_date);

      if (startRaw && endRaw) {
        const plainDate = (val) => typeof val === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(val);
        // Parse start/end using LOCAL dates when given as YYYY-MM-DD to avoid UTC shifts
        const parseLocal = (val) => {
          if (plainDate(val)) {
            const [y, m, d] = val.split('-').map(Number);
            return new Date(y, m - 1, d, 0, 0, 0, 0);
          }
          return new Date(val);
        };
        const s = parseLocal(startRaw);
        let e = parseLocal(endRaw);
        const ONE_DAY = 24 * 60 * 60 * 1000;
        if (!Number.isNaN(s.getTime()) && !Number.isNaN(e.getTime())) {
          const sStart = new Date(s.getFullYear(), s.getMonth(), s.getDate(), 0, 0, 0, 0);
          const diff = e.getTime() - s.getTime();
          const midnightIso = (val) => typeof val === 'string' && /T00:00:00(?:\.\d{3})?(?:Z|[+\-]\d{2}:\d{2})?$/.test(val);
          const isExclusive = diff >= ONE_DAY && (plainDate(endRaw) || midnightIso(endRaw) || (e.getHours() === 0 && e.getMinutes() === 0 && e.getSeconds() === 0));
          if (isExclusive) {
            if (plainDate(endRaw)) {
              // If provided as date-only and exclusive, set to previous day's end explicitly
              e = new Date(e.getFullYear(), e.getMonth(), e.getDate() - 1, 23, 59, 59, 999);
            } else {
              // Generic fallback: step back 1 ms
              e = new Date(e.getTime() - 1);
            }
          }
        }
        const out = { from: fmt(s, false), to: fmt(e, true) };
        try { console.debug('Timeline.normalizeRange ->', { startRaw, endRaw, out }); } catch (e2) {}
        return out;
      }

      if (range instanceof Date || ['string', 'number'].includes(typeof range)) {
        return { from: fmt(range, false), to: fmt(range, true) };
      }

      return { from: null, to: null };
    },

    async onCustomSelected() {
      // Ensure whatever DatePicker stored is normalized, then trigger fetch and close overlay
      const current = this.$store.getters.getTimeFrame;
      const normalized = this.normalizeRange(current || {});
      if (normalized.from && normalized.to) {
        this.$store.commit('setTimeFrame', normalized);
      } else {
        return responses.showWarning('Invalid Time Frame');
      }

      this.setActiveTab(0);
      $utils.showLoading();
      try {
        return await Promise.all([this.methodBeingCalled()]);
      } finally {
        $utils.hideLoading();
      }
    },

    async setTimeFrame(tab) {
      this.setActiveTab(tab);
      let range;
      switch (tab) {
        case 1:
          range = TimeManager.getToday();
          break;
        case 2:
          range = TimeManager.getYesterday();
          break;
        case 3:
          range = TimeManager.getLastWeek();
          break;
        case 4:
          range = TimeManager.getPreviousMonth();
          break;
        case 5:
          range = TimeManager.getThisMonth();
          break;
        case 6:
          range = TimeManager.getThisYear();
          break;
        case 7: {
          const customRange = this.$store.getters.getTimeFrame;
          range = customRange || {};
          break;
        }
        default:
          range = TimeManager.getThisMonth();
          break;
      }

      const normalized = this.normalizeRange(range);
      if (!normalized.from || !normalized.to) {
        return responses.showWarning('Invalid Time Frame');
      }
      // Commit ONLY normalized { from, to }
      this.$store.commit('setTimeFrame', normalized);

      $utils.showLoading();
      try {
        return await Promise.all([this.methodBeingCalled()]);
      } finally {
        $utils.hideLoading();
      }
    },

    setActiveTab(tab) {
      this.$store.dispatch("updateActiveTimeTab", tab);
      this.activeTab = tab;
    },
  },
  watch: {
    // Watcher to keep activeTab in sync with Vuex store updates
    activeTimeTab(newActiveTimeTab) {
      this.activeTab = newActiveTimeTab;
    },
  },
};
</script>

<style scoped>
.timeline-container {
  padding: 0 10px;
  border-radius: var(--radius-card);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  z-index: 1;
  background-color: var(--card-bg);
}

.timeline-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.timeline-header h1 {
  margin: 0;
  color: var(--text-primary);
  font-size: 24px;
  font-weight: 600;
}

.online-status {
  background-color: var(--accent-primary);
  border: none;
  border-radius: var(--radius-button);
  color: #fff;
  padding: 10px 20px;
  margin-left: 20px;
  cursor: pointer;
  font-size: 16px;
}

.online-status:hover {
  background-color: var(--accent-primary-hover);
  color: #fff;
}

.selected-timeline {
  background-color: var(--card-bg);
  color: var(--text-secondary);
  margin-top: 10px;
  padding: 5px 8px;
  border-radius: var(--radius-card);
  border: 1px solid var(--grid-lines);
}

.timeline-buttons {
  display: flex;
  align-items: center;
  gap: 10px;
}

.mobile-search {
  display: flex;
  justify-content: end;
  background: none;
}

@media only screen and (max-width: 950px) {
  .timeline {
    margin-bottom: 0.5rem !important;
  }

  .desktop {
    display: none !important;
  }

  .timeline-buttons-mobile {
    display: flex;
    flex-direction: column;
    justify-content: center;
    background-color: var(--card-bg);
  }

  .timeline-container-mobile {
    display: block !important;
  }

  .timeline-buttons {
    display: flex;
    flex-wrap: nowrap;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }

  .timeline-buttons::-webkit-scrollbar {
    display: none;
  }

  .timeline-btn {
    border-radius: var(--radius-button);
    color: white;
    padding: 5px 15px;
    flex: 1;
  }
}
</style>
